import {
  Fragment,
  useEffect,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { ErrorPage, Progress } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { coreExtensionData } from '@backstage/frontend-plugin-api';
import { richardAdminPlugin } from '@abstract/plugin-richard-admin';

export const RICHARD_ADMIN_GROUP = 'group:default/role-backstage-admin';

export function useIsRichardAdminAuthorized() {
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState({ loading: true, allowed: false });

  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (active) {
          setState({
            loading: false,
            allowed: identity.ownershipEntityRefs.includes(RICHARD_ADMIN_GROUP),
          });
        }
      })
      .catch(() => {
        if (active) {
          setState({ loading: false, allowed: false });
        }
      });
    return () => {
      active = false;
    };
  }, [identityApi]);

  return state;
}

export function RichardAdminAccessGate({ children }: PropsWithChildren) {
  const { loading, allowed } = useIsRichardAdminAuthorized();

  if (loading) {
    return <Progress />;
  }

  if (!allowed) {
    return (
      <ErrorPage
        status="403"
        statusMessage="Accesso ristretto agli amministratori Backstage"
        additionalInfo={`È richiesta l'appartenenza a ${RICHARD_ADMIN_GROUP}.`}
      />
    );
  }

  return <Fragment>{children}</Fragment>;
}

const richardAdminPage = richardAdminPlugin
  .getExtension('page:richard-admin')
  .override({
    factory(originalFactory) {
      const originalOutput = originalFactory();
      // The page extension is typed as ExtensionDefinition<any>, so its element comes back as unknown
      const originalPage = originalOutput.get(
        coreExtensionData.reactElement,
      ) as ReactNode;

      return Array.from(originalOutput, output =>
        output.id === coreExtensionData.reactElement.id
          ? coreExtensionData.reactElement(
              <RichardAdminAccessGate>{originalPage}</RichardAdminAccessGate>,
            )
          : output,
      );
    },
  });

export const richardAdminFeature = richardAdminPlugin.withOverrides({
  extensions: [richardAdminPage],
});
